import React from "react";
import { ObfuscatedEmail } from "../components/ObfuscatedEmail";

export const PrivacyPolicy: React.FC = () => {
  return (
    <section className="secondary-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 sm:mb-12 lg:mb-12">
          <h1>Privacy Policy</h1>
          <p className="edited-date">Effective date: 2nd of September 2026</p>
        </div>
        <div className="mb-8 sm:mb-12 lg:mb-12">
          <p className="l0-section-text">
            This Privacy Policy explains how{" "}
            <span className="logo-font">LAB ZER0</span> Pty Ltd ("
            <span className="logo-font">LAB ZER0</span>," "we," "us," or "our")
            collects, uses, discloses, and safeguards information when you or
            your organisation use our subscription platform (the "Platform"). By
            accessing or using the Platform, you agree to the practices
            described in this Policy.
          </p>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">1. Information We Collect</h2>
          <div className="l0-section-text">
            <p>We collect and process the following types of information:</p>
            <ul className="l0-list">
              <li>
                <b className="l0-emphasis">Account information:</b> name, email
                address, organisation details.
              </li>
              <li>
                <b className="l0-emphasis">Technical information:</b>{" "}
                automatically logged information such as IP address, browser
                type, operating system, device details, and general activity for
                analytics and security purposes.
              </li>
              <li>
                <b className="l0-emphasis">Usage log information:</b> server log
                information including web requests, interactions with the
                Platform, referring and exit pages or URLs, number of clicks,
                how you interact with features and links, domain names, landing
                pages, and pages viewed.
              </li>
            </ul>
            <p>
              This information may be collected in anonymous or aggregated form
              and is used to help us improve the Platform, monitor performance,
              maintain security, and protect against misuse.
            </p>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">2. How We Use Information</h2>
          <div className="l0-section-text">
            <p>We use the information we collect to:</p>
            <ul className="l0-list">
              <li>Provide, maintain, and improve the Platform.</li>
              <li>
                Send you newsletters if you subscribed to them, or send you
                important email correspondence.
              </li>
              <li>
                Generate personalised estimate impact reports for organisations,
                based on user inputs and backend algorithms.
              </li>
              <li>
                Conduct analytics to understand Platform usage and improve
                functionality.
              </li>
              <li>Respond to support requests and provide customer service.</li>
              <li>
                Monitor compliance with these Terms and investigate potential
                misuse.
              </li>
              <li>
                Publish your achievement on our website, only if you explicitly
                gave us permission.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">
            3. De-identified Data & Benchmarking
          </h2>
          <div className="l0-section-text">
            <p>We may use aggregated, de-identified data for:</p>
            <ul className="l0-list">
              <li>Internal analytics and platform improvement.</li>
              <li>Academic or applied research projects.</li>
              <li>
                Marketing purposes (e.g., publishing generalised sustainability
                trends).
              </li>
            </ul>
            <p>
              At no point will your organisation's name, domain, or address be
              disclosed in such contexts.
            </p>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">4. Data Retention & Security</h2>
          <div className="l0-section-text">
            <p>
              We retain data only as long as necessary to provide you access to
              the Platform or as required by law. Organisations may request
              deletion of their data at any time.
            </p>
            <p>
              All platform data is stored on secure facilities located in
              Australia.
            </p>
            <p>We implement industry-standard safeguards, including:</p>
            <ul className="l0-list">
              <li>
                Two-Factor Authentication (2FA) for all administrative access.
              </li>
              <li>
                Role based access control, based on Principle of Least
                Privilege.
              </li>
              <li>
                Firewall ACLs to limit public exposure to sensitive areas.
              </li>
              <li>Hashed password storage.</li>
              <li>
                Keeping our software and infrastructure up-to-date with the
                latest security patches.
              </li>
              <li>Regular monitoring and auditing of access logs.</li>
              <li>
                Technical and organisational measures to prevent unauthorised
                access, misuse, loss, or disclosure.
              </li>
              <li>
                Multiple copies of backups, stored in geographically dispersed
                locations.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">5. Breach Notification</h2>
          <div className="l0-section-text">
            <p>
              In the event of a data breach,{" "}
              <span className="logo-font">LAB ZER0</span> will:
            </p>
            <ul className="l0-list">
              <li>
                Comply with the Australian Notifiable Data Breaches (NDB)
                scheme.
              </li>
              <li>
                Notify affected organizations promptly if their information is
                involved.
              </li>
              <li>
                Provide details of the breach and steps taken to mitigate risk.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">
            6. Third-Party Service Providers
          </h2>
          <div className="l0-section-text">
            <p>
              We use recognized third-party providers (such as hosting or
              analytics services):
            </p>
            <ul className="l0-list">
              <li>
                They are contractually ogligated to uphold equivalent data
                protection standards.
              </li>
              <li>
                Data is not transferred outside Australia unless necessary and
                with appropriate safeguards in place.
              </li>
            </ul>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">7. User Responsibilities</h2>
          <div className="l0-section-text">
            <p>Users are responsible for:</p>
            <ul className="l0-list">
              <li>Keeping their login credentials confidential.</li>
              <li>
                Not sharing their accounts or access with unauthorised
                individuals.
              </li>
              <li>Enabling 2FA where available.</li>
              <li>
                Ensuring the devices they use to access the Platform are secure,
                up-to-date, and free of malware.
              </li>
            </ul>
            <p>
              <span className="logo-font">LAB ZER0</span> is not responsible for
              breaches caused by compromised user devices or credential misuse.
            </p>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">
            8. Access, Correction & Deletion
          </h2>
          <div className="l0-section-text">
            <p>Organisations may request:</p>
            <ul className="l0-list">
              <li>Access to the information stored on their behalf.</li>
              <li>Correction of inaccurate information.</li>
              <li>
                Deletion of data, subject to legal or contractual obligations.
              </li>
            </ul>
            <p>
              Requests can be made by contacting us at{" "}
              <ObfuscatedEmail email="privacy@labzero.eco" />
            </p>
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">9. Changes to This Policy</h2>
          <div className="l0-section-text">
            We may update this Privacy Policy from time to time. Any material
            changes will be notified to registered organizations. Continued use
            of the Platform after such changes constitutes acceptance of the
            updated policy.
          </div>
        </div>

        <div className="l0-faq-item">
          <h2 className="l0-section-heading">10. Contact Us</h2>
          <div className="l0-section-text">
            <p>
              For questions or concerns about this Privacy Policy, please
              contact:
            </p>
            <div>
              <p>
                <span className="logo-font">LAB ZER0</span> Pty Ltd
              </p>
              <p>ABN: 616 865 62185</p>
              <p>
                Email: <ObfuscatedEmail email="privacy@labzero.eco" />
              </p>
              <p>23, 500 Collins Street, Melbourne, VIC 3005</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

PrivacyPolicy.displayName = "PrivacyPolicy";
